import { PlayRow } from 'models/PlayEditRow/PlayRow';
import { makeAutoObservable } from 'mobx';
import { ISecondSourceBoxDiscrepancy, ISecondSourcePlay } from 'interfaces/secondSourceBox/ISecondSourcePlay';
import { IHighlightDisplayConfig } from '@trackman/baseball-shared-components/build/services/DataGrid/types/IHighlightDisplayConfig';
import { ISectionHighlight } from '@trackman/baseball-shared-components/build/services/DataGrid/types/ISectionHighlight';
import { v1 as uuid } from 'uuid';
import { emptyBoxScore } from 'constants/Constants';
import { IBoxScoreResponse } from 'interfaces/boxScore/IBoxScoreResponse';
import { PositionDict } from 'enums/PositionDictionary';
import { PlaysGridRowManager } from './PlaysGridRowManager';

const positions = [
  'firstBase',
  'secondBase',
  'thirdBase',
  'firstBaseman',
  'secondBaseman',
  'thirdBaseman',
  'shortStopper',
  'leftFielder',
  'centerFielder',
  'rightFielder',
];

export class SecondSourceBoxManager {
  static SecondSourceHighlightGroupName = 'secondSourceBox';

  constructor(rowManager: PlaysGridRowManager) {
    this.rowManager = rowManager;
    makeAutoObservable(this);
  }

  private rowManager: PlaysGridRowManager;

  trackmanBoxScore: IBoxScoreResponse = { ...emptyBoxScore };

  secondSourceBoxScore: ISecondSourcePlay[] = [];

  isDataLoaded = false;

  selectedPlayer: ISecondSourceBoxDiscrepancy | undefined = undefined;

  discrepancies: ISecondSourceBoxDiscrepancy[] = [];

  highlightNavigationButtonRenderer?: (config: IHighlightDisplayConfig) => React.ReactElement;

  updateBoxScore(data?: ISecondSourcePlay[]): void {
    this.secondSourceBoxScore = data || this.secondSourceBoxScore;
    this.discrepancies = this.getDiscrepancies();
    this.isDataLoaded = true;
  }

  clearSelectedPlayers(): void {
    this.selectedPlayer = undefined;
    this.recalculateHighlights();
  }

  private isColumnIgnored = (row: PlayRow, position: string): boolean => {
    return row.ignoredColumns.includes(position);
  };

  private isPlayerIncludesForeignId = (row: PlayRow, position: string, secondSourcePlay: ISecondSourcePlay): boolean => {
    const foreignIds = row[position]?.foreignIds?.map((id: string) => id.split('-').pop());
    return foreignIds?.includes(secondSourcePlay[position].foreignId);
  };

  private playerHasSameName = (row: PlayRow, position: string, secondSourcePlay: ISecondSourcePlay): boolean => {
    if (secondSourcePlay[position] && row[position]) {
      const [firstName, lastName] = secondSourcePlay[position].nameRef.split(' ');

      return row[position].nameRef === `${lastName}, ${firstName}`;
    }

    return false;
  };

  private isBothPlayersUndefined = (row: PlayRow, position: string, secondSourcePlay: ISecondSourcePlay): boolean => {
    return row[position] && row[position].position === '' && !secondSourcePlay[position];
  };

  private comparePlayers = (): void => {
    this.secondSourceBoxScore.forEach((secondSourcePlay) => {
      const row = this.rowManager.rows.find((trackmanPlay) => trackmanPlay.playId === secondSourcePlay.playId);

      if (row && !row.isWarmup) {
        positions.forEach((position) => {
          if (this.isBothPlayersUndefined(row, position, secondSourcePlay)) {
            return;
          }

          if (!this.isColumnIgnored(row, position) && !secondSourcePlay[position] && row[position] !== secondSourcePlay[position]) {
            this.discrepancies.push({
              foreignId: '',
              nameRef: 'Undefined',
              pitchId: this.rowManager.findIndex(row.id),
              position,
              teamName: '',
              playSequenceNumber: row.playSequenceNumber,
            });

            this.rowManager.rows[this.rowManager.findIndex(row.id)].status.errors.push({
              dataKey: position,
              errorMessage: `Error on ${PositionDict[position]}, should be "Undefined"`,
            });
            return;
          }

          if (
            !this.isColumnIgnored(row, position) &&
            !this.isPlayerIncludesForeignId(row, position, secondSourcePlay) &&
            row[position] !== secondSourcePlay[position] &&
            !this.playerHasSameName(row, position, secondSourcePlay)
          ) {
            this.discrepancies.push({
              foreignId: secondSourcePlay[position as keyof ISecondSourcePlay]?.foreignId,
              nameRef: secondSourcePlay[position as keyof ISecondSourcePlay]?.nameRef,
              pitchId: this.rowManager.findIndex(row.id),
              position,
              teamName: secondSourcePlay[position as keyof ISecondSourcePlay]?.teamName,
              playSequenceNumber: row.playSequenceNumber,
            });

            this.rowManager.rows[this.rowManager.findIndex(row.id)].status.errors.push({
              dataKey: position,
              errorMessage: `Error on ${PositionDict[position]}, should be "${secondSourcePlay[position].nameRef}"`,
            });
          }
        });
      }
    });
  };

  getDiscrepancies = (): ISecondSourceBoxDiscrepancy[] => {
    this.discrepancies = [];
    const t0 = performance.now();

    this.comparePlayers();

    const t1 = performance.now();
    console.log(`Checking discrepancies took ${t1 - t0} ms.`);

    return this.discrepancies;
  };

  selectPlayer = (player: ISecondSourceBoxDiscrepancy): void => {
    this.selectedPlayer = player;
    this.recalculateHighlights(player);
  };

  recalculateHighlights(player?: ISecondSourceBoxDiscrepancy): void {
    const highlights = [];
    this.rowManager.highlightManager.removeGroup(SecondSourceBoxManager.SecondSourceHighlightGroupName);

    let currentPlayerHighlight: ISectionHighlight | undefined;

    if (player && player.pitchId) {
      if (currentPlayerHighlight) {
        currentPlayerHighlight.toRowIndex = player.pitchId;
      } else {
        currentPlayerHighlight = {
          id: uuid(),
          groupId: SecondSourceBoxManager.SecondSourceHighlightGroupName,
          fromRowIndex: player.pitchId,
          toRowIndex: player.pitchId,
          columnIndex: this.rowManager.columns.findIndex((c) => c.dataKey === player.position),
          className: 'player-highlight',
          customElementRender: this.highlightNavigationButtonRenderer,
        };
      }
    } else if (currentPlayerHighlight) {
      highlights.push(currentPlayerHighlight);
      currentPlayerHighlight = undefined;
    }

    if (currentPlayerHighlight) {
      highlights.push(currentPlayerHighlight);
    }

    this.rowManager.highlightManager.setHighlights(highlights);

    if (player && player.pitchId) {
      this.rowManager.scrollToRow(player.pitchId);
      this.rowManager.scrollToColumn(player.position);
    }
    this.rowManager.updateGrid();
  }
}
